import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, Star } from "lucide-react";

const EMPTY = { name: "", role: "", rating: 5, text: "", published: true };

export default function AdminTestimonials() {
  const [items, setItems] = useState(null);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.name || !form.text) { toast.error("Name and text required"); return; }
    const payload = { ...form, rating: Number(form.rating) || 5 };
    if (editing) await api.put(`/admin/testimonials/${editing.id}`, payload);
    else await api.post("/admin/testimonials", payload);
    toast.success("Saved"); setOpen(false); load();
  };
  const del = async (t) => { if (!window.confirm("Delete this testimonial?")) return; await api.delete(`/admin/testimonials/${t.id}`); toast.success("Deleted"); load(); };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-display text-3xl text-navy">Testimonials</h1><p className="text-muted-foreground mt-1">Manage customer reviews.</p></div>
        <Button onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }} data-testid="add-testimonial-btn" className="bg-gold text-navy hover:bg-gold-dark font-semibold"><Plus className="w-4 h-4 mr-1" /> Add Testimonial</Button>
      </div>
      {items === null ? <Skeleton className="h-64 rounded-2xl" /> : (
        <div className="grid md:grid-cols-2 gap-4">
          {items.map((t) => (
            <div key={t.id} className="bg-white rounded-2xl border border-gray-100 shadow-card p-5" data-testid={`testimonial-admin-${t.id}`}>
              <div className="flex justify-between items-start">
                <div><p className="font-semibold text-navy">{t.name}</p><p className="text-xs text-gold">{t.role}</p></div>
                <div className="flex gap-1"><Button size="icon" variant="ghost" onClick={() => { setEditing(t); setForm({ ...EMPTY, ...t }); setOpen(true); }}><Pencil className="w-4 h-4" /></Button><Button size="icon" variant="ghost" onClick={() => del(t)} className="text-red-500"><Trash2 className="w-4 h-4" /></Button></div>
              </div>
              <div className="flex gap-0.5 my-2">{Array.from({ length: t.rating || 5 }).map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-gold text-gold" />)}</div>
              <p className="text-sm text-muted-foreground">"{t.text}"</p>
              <span className={`text-xs mt-2 inline-block px-2 py-0.5 rounded-full ${t.published ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>{t.published ? "Published" : "Hidden"}</span>
            </div>
          ))}
          {items.length === 0 && <p className="text-center text-muted-foreground py-10 md:col-span-2">No testimonials yet.</p>}
        </div>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-display text-navy">{editing ? "Edit" : "Add"} Testimonial</DialogTitle></DialogHeader>
          <div><Label className="text-xs font-semibold text-navy">Name</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} className="bg-gray-50 mt-1" data-testid="tf-name" /></div>
          <div><Label className="text-xs font-semibold text-navy">Role / Location</Label><Input value={form.role} onChange={(e) => set("role", e.target.value)} className="bg-gray-50 mt-1" /></div>
          <div><Label className="text-xs font-semibold text-navy">Rating (1-5)</Label><Input type="number" min={1} max={5} value={form.rating} onChange={(e) => set("rating", e.target.value)} className="bg-gray-50 mt-1" /></div>
          <div><Label className="text-xs font-semibold text-navy">Testimonial Text</Label><Textarea value={form.text} onChange={(e) => set("text", e.target.value)} className="bg-gray-50 mt-1" rows={3} data-testid="tf-text" /></div>
          <div className="flex items-center gap-2"><Switch checked={form.published} onCheckedChange={(v) => set("published", v)} /><Label className="text-sm">Published</Label></div>
          <Button onClick={save} data-testid="save-testimonial-btn" className="bg-gold text-navy hover:bg-gold-dark font-semibold">Save</Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
